import { type Game, type UseGameAssetParameters, GameAssetTypes, GameTileType, type GameAssetTypesUnion } from '@game/types'
import { useGameConfig, useCoreConfig, createError, useFormat, ref, computed, onMounted, useNetwork } from '#imports'

// `fallback` must resolve to a non-empty string. The function form is for
// callers whose fallback comes from a reactive theme ref (resolved fresh
// on each read); callers commit to the brand contract that the slot is
// defined (asserted with `!` at the call site). An empty fallback would
// render `<NuxtImg src="">` — a broken-image placeholder, never desirable.
export function useAssetFallback(getPrimary: () => string | null, fallback: string | (() => string)) {
    const errored = ref(false)
    const resolveFallback = typeof fallback === 'string' ? () => fallback : fallback
    const src = computed(() => errored.value ? resolveFallback() : (getPrimary() ?? resolveFallback()))
    const isFallback = computed(() => src.value === resolveFallback())

    return {
        src,
        isFallback,
        onError: () => { errored.value = true },
        reset: () => { errored.value = false },
    }
}

export const useGameAssets = () => {
    const { assetsUrl, assetsSiteFolder, brandGameAssets } = useGameConfig()
    const { company } = useCoreConfig()
    const { asDatetime } = useFormat()

    const loadVideo = ref(true)

    if (import.meta.client) {
        const { effectiveType } = useNetwork()

        onMounted(() => {
            loadVideo.value = !(effectiveType.value && ['slow-2g', '2g'].includes(effectiveType.value))
        })
    }

    function isVideoTile(game: Game | undefined, videoEnabled: boolean) {
        if (!loadVideo.value || !videoEnabled) return false
        if (!hasAsset(game, GameAssetTypes.VIDEO)) return false

        return game?.tile_type === GameTileType.VIDEO
    }

    function isVideoSmallTile(game: Game | undefined, videoEnabled: boolean) {
        if (!loadVideo.value || !videoEnabled) return false
        if (!hasAsset(game, GameAssetTypes.VIDEO_SMALL)) return false

        return game?.tile_type === GameTileType.VIDEO_SMALL
    }

    function getFileExtension(gameAssetType: GameAssetTypesUnion) {
        if (gameAssetType === GameAssetTypes.VIDEO || gameAssetType === GameAssetTypes.VIDEO_SMALL || gameAssetType === GameAssetTypes.HERO_VIDEO) {
            return 'mp4'
        }

        if (gameAssetType === GameAssetTypes.HERO_BACKGROUND) {
            return 'jpg'
        }

        return 'png'
    }

    function assetUrl(game: UseGameAssetParameters | Game | undefined | null, type: GameAssetTypesUnion = GameAssetTypes.LOGO, external?: boolean) {
        if (!game) {
            return ' ' // Return string with a space, since nuxt-image will throw an error.
        }

        const { slug, vendor_slug, provider_slug, updated_at } = game

        if (!slug || !vendor_slug || !provider_slug || !updated_at) {
            console.log('useGameAssets - assetsUrl: game missing one of these props: slug, vendor_slug, provider_slug, updated_at')

            return ' '
        }

        if (!company?.websiteName) {
            throw createError({ statusCode: 500, statusMessage: 'useGameAssets: Missing core value: company.websiteName is not set' })
        }

        const assetExtension = getFileExtension(type)
        // Create timestamp from year (YY) month (M) day (D) hour (H) minut (m) secound (s)
        const timestamp = asDatetime(updated_at, { format: 'YYMDHMms' })
        let siteFolder = assetsSiteFolder || company.websiteName.toLowerCase()

        if (brandGameAssets) {
            // Check if game has Brand Game Assets
            const { slugs, folderName } = brandGameAssets

            if (slugs && slugs.includes(slug)) {
                siteFolder = folderName
            }
        }

        const url = `${external ? assetsUrl : ''}/game_tiles/${provider_slug}/${vendor_slug}/${slug}/${siteFolder}/${type}.${assetExtension}?t=${timestamp}`

        return url
    }

    function mediaCdnUrl(path: string, width: number, height?: number, quality = 65) {
        const options = {
            w: Math.round(width / 2) * 2,
            h: height ? Math.round(height / 2) * 2 : undefined,
            q: quality,
        }

        const optionsString = Object.entries(options)
            .filter(([_, value]) => value)
            .map(([key, value]) => `${key}=${value}`)
            .join(',')

        return `/cdn-cgi/media/${optionsString}${path}`
    }

    function videoAssetUrl(game: UseGameAssetParameters | Game | undefined | null, type: GameAssetTypesUnion = GameAssetTypes.VIDEO, width?: number, height?: number, quality?: number) {
        const path = assetUrl(game, type)

        if (!path || path === ' ' || !width) {
            return assetUrl(game, type, true)
        }

        return `${assetsUrl}${mediaCdnUrl(path, width, height, quality)}`
    }

    // Strict: only render when the server has explicitly confirmed the asset
    // exists. `null` (server hasn't checked yet) and `undefined` (legacy
    // response without the field) are treated as "no" — generating a URL we
    // can't be sure resolves causes a 404 flash before the fallback kicks in.
    function hasAsset(game: Game | undefined | null, type: GameAssetTypesUnion): boolean {
        return game?.asset_availability?.[type] === true
    }

    function firstAvailable(game: Game | undefined | null, ...types: GameAssetTypesUnion[]): string | null {
        for (const type of types) {
            if (hasAsset(game, type)) return assetUrl(game, type)
        }
        return null
    }

    return {
        assetUrl,
        videoAssetUrl,
        mediaCdnUrl,
        loadVideo,
        isVideoTile,
        isVideoSmallTile,
        hasAsset,
        firstAvailable,
    }
}
